import React, { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';
import './Apply.css';
import { FaTimes, FaUser, FaEnvelope, FaPhone, FaGraduationCap, FaPaperPlane } from 'react-icons/fa';

const EnquiryForm = ({ closeModal }) => {
  const formRef = useRef();
  
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    course: '',
  });

  const [isFormValid, setIsFormValid] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Email JS credentials
  const SERVICE_ID = "service_8xglq8s"; // Your service ID
  const TEMPLATE_ID = "template_ei2x3br"; // Your template ID
  const PUBLIC_KEY = "DoJf92tTCcHRptTv5"; // Your public key

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear messages when user starts typing
    setSuccessMessage('');
    setErrorMessage('');
  };

  useEffect(() => {
    const valid = Object.values(formData).every((v) => v.trim() !== '');
    setIsFormValid(valid);
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Initialize EmailJS
      emailjs.init(PUBLIC_KEY);
      
      // Prepare template parameters
      const templateParams = {
        from_name: `${formData.firstname} ${formData.lastname}`,
        from_email: formData.email,
        phone: formData.phone,
        course: formData.course,
        message: `New enquiry from ${formData.firstname} ${formData.lastname}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nCourse: ${formData.course}`,
        to_email: 'ptlrct@gmail.com', // College email
        reply_to: formData.email
      };

      // Send email
      const result = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        templateParams,
        PUBLIC_KEY
      );

      if (result.text === 'OK') {
        setSuccessMessage('Thank you for your enquiry! Our counselor will contact you soon.');
        setErrorMessage('');
        setFormData({ firstname: '', lastname: '', email: '', phone: '', course: '' });
        
        // Close modal after 3 seconds on success
        setTimeout(() => {
          closeModal();
        }, 3000);
      } else {
        throw new Error('Failed to send email');
      }
      
    } catch (err) {
      console.error('Email JS Error:', err);
      setErrorMessage('Unable to submit form. Please try again or contact us directly.');
      setSuccessMessage('');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close modal when clicking outside
  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('enquiry-overlay')) {
      closeModal();
    }
  };

  return (
    <div className="enquiry-overlay" onClick={handleOverlayClick}>
      <div className="enquiry-modal">
        {/* Header with gradient */}
        <div className="enquiry-header">
          <h2 className="enquiry-title">
            <span className="title-icon">📋</span>
            Enquiry Form
          </h2>
          <button className="enquiry-close" onClick={closeModal}>
            <FaTimes />
          </button>
        </div>

        {/* Status Messages */}
        {successMessage && (
          <div className="enquiry-message success">
            <span className="message-icon">✓</span>
            {successMessage}
          </div>
        )}
        
        {errorMessage && (
          <div className="enquiry-message error">
            <span className="message-icon">⚠</span>
            {errorMessage}
          </div>
        )}

        {/* Form */}
        <form ref={formRef} onSubmit={handleSubmit} className="enquiry-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstname" className="form-label">
                <FaUser className="input-icon" />
                First Name
              </label>
              <input
                type="text"
                id="firstname"
                name="firstname"
                className="form-input"
                placeholder="Enter your first name"
                value={formData.firstname}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastname" className="form-label">
                <FaUser className="input-icon" />
                Last Name
              </label>
              <input
                type="text"
                id="lastname"
                name="lastname"
                className="form-input"
                placeholder="Enter your last name"
                value={formData.lastname}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              <FaEnvelope className="input-icon" />
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="form-input"
              placeholder="Enter your email address"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone" className="form-label">
              <FaPhone className="input-icon" />
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              className="form-input"
              placeholder="Enter your 10-digit phone number"
              value={formData.phone}
              onChange={handleInputChange}
              required
              pattern="[0-9]{10}"
              title="Please enter a valid 10-digit phone number"
            />
          </div>

          <div className="form-group">
            <label htmlFor="course" className="form-label">
              <FaGraduationCap className="input-icon" />
              Select Course
            </label>
            <select
              id="course"
              name="course"
              className="form-select"
              value={formData.course}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Choose your course --</option>
              <option value="Diploma in Engineering">Diploma in Engineering</option>
              <option value="B.Tech (Bachelor of Technology)">B.Tech (Bachelor of Technology)</option>
              <option value="M.Tech (Master of Technology)">M.Tech (Master of Technology)</option>
              <option value="MBA (Master of Business Administration)">MBA (Master of Business Administration)</option>
            </select>
          </div>

          <button 
            type="submit" 
            className={`form-submit ${isSubmitting ? 'submitting' : ''}`}
            disabled={!isFormValid || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                Submitting...
              </>
            ) : (
              <>
                <FaPaperPlane className="submit-icon" />
                Submit Enquiry
              </>
            )}
          </button>
        </form>

        {/* Footer Note */}
        <p className="form-footer">
          Our counselor will contact you within 24 hours
        </p>
      </div>
    </div>
  );
};

export default EnquiryForm;